import { useCallback, useMemo, useState } from 'react';

type SetTrackedState<T> = (value: T | ((prev: T) => T)) => void;

type UseTrackedStateReturn<T> = {
  state: T;
  setState: SetTrackedState<T>;
  undo: () => void;
  redo: () => void;
  reset: (initialValue: T) => void;
  canUndo: boolean;
  canRedo: boolean;
  currentStep: number;
  history: T[];
};

export function useTrackedStateWithUndoRedo<T>(
  initialState: T,
): UseTrackedStateReturn<T> {
  const MAX_HISTORY = 10;

  const [historyState, setHistoryState] = useState<{
    history: T[];
    currentStep: number;
  }>({
    history: [initialState],
    currentStep: 0,
  });

  const { history, currentStep } = historyState;

  const state = useMemo(() => history[currentStep], [history, currentStep]);

  const setState: SetTrackedState<T> = useCallback((valueOrUpdater) => {
    setHistoryState((prev) => {
      const currentValue = prev.history[prev.currentStep];
      const newValue =
        typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: T) => T)(currentValue)
          : valueOrUpdater;

      let newHistory = prev.history.slice(0, prev.currentStep + 1);
      newHistory.push(newValue);

      if (newHistory.length > MAX_HISTORY) {
        newHistory = newHistory.slice(1);
      }

      return {
        history: newHistory,
        currentStep: newHistory.length - 1,
      };
    });
  }, []);

  const undo = useCallback(() => {
    setHistoryState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const redo = useCallback(() => {
    setHistoryState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.history.length - 1, prev.currentStep + 1),
    }));
  }, []);

  const reset = useCallback((initial: T) => {
    setHistoryState({
      history: [initial],
      currentStep: 0,
    });
  }, []);

  return {
    state,
    setState,
    undo,
    redo,
    reset,
    canUndo: currentStep > 0,
    canRedo: currentStep < history.length - 1,
    currentStep,
    history,
  };
}
